import React, {useCallback, useMemo, useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import Modal from '../Modal/Modal';
import RelateItemList from '../RelateList/RelateItemList';
import {labels, currentResource, isNullOrUndefined, isNullOrWhitespace} from '../../../util/utils';
import {modalLevelToast, specialBodyExtendedPagingOptions} from '../../../util/uiconstants';
import {useEngagementId, useDocumentId} from '../../../util/customHooks';
import {getAllControls} from '../../../actions/Control/controlaction';
import {getAllWCGWs} from '../../../actions/WCGW/wcgwactions';
import {
	relateControlToFlowChartStep,
	unrelateControlFromFlowChartStep,
	updateStoreRelatedFlowchartStepControl,
	updateStoreUnrelatedControlFromFlowchartStep
} from '../../../actions/FlowChart/flowchartstepactions';
import {clearErrors} from '../../../actions/erroractions';
import {setFormV2ToasterVisibility} from '../../../actions/toastactions';

export default function RelateFlowchartControlModel({isModalOpen, onClickRelateModal, stepName}) {
	const dispatch = useDispatch();
	const entityUIDInt = useSelector((state) => state.flowChart?.data[0]?.entityUIDInt);
	const flowchartStep = useSelector((state) => state.flowchartStep);
	const flowchartStepName = useSelector((state) => state.flowchartStep.flowchartStepName);
	const wcgws = useSelector((state) => state.wcgws);
	const flowchartId = useSelector((state) => state.flowChart.data[0].id);
	const flowchartStepId = useSelector((state) => state.flowchartStep.id);

	const engagementId = useEngagementId();
	const documentId = useDocumentId();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(specialBodyExtendedPagingOptions.defaultPageSize);
	const [haswcgw, setHasWcgw] = useState(false);

	useEffect(() => {
		dispatch(clearErrors());
		dispatch(setFormV2ToasterVisibility(false));
	}, []);

	const flowchartControlRef = useRef(
		new Set(
			useSelector((state) => {
				const controlSet = (state.flowchartStep.flowchartStepsControl || [])?.map(
					(controlItem) => controlItem.controlId
				);
				return controlSet;
			})
		)
	);

	const selected = useCallback(
		(entity) => {
			return flowchartControlRef.current.has(entity.id);
		},
		[flowchartControlRef]
	);

	useEffect(() => {
		dispatch(getAllWCGWs(engagementId)).then(() => {
			wcgws?.length > 0 ? setHasWcgw(true) : setHasWcgw(false);
		});
	}, []);

	const renderItemComponent = useCallback((controls) => {
		return <EllipsesControl noOfLines={1} content={controls.controlName} tooltip={controls.controlName} />;
	}, []);

	const componentHeader = useCallback(() => {
		return (
			<section className="filter-wcgw-container">
				<section className="related-wcgw">
					<EllipsesControl noOfLines={1} content={stepName} tooltip={stepName} />
				</section>
			</section>
		);
	}, []);

	const relateListHeaders = useCallback(
		(totalEntityCount) => (
			<section className="wcgw-header">
				<section className="wcgw-name">{labels.flowChartNewSteps.headerNameForControl}</section>
				<section className="wcgw-count">
					{totalEntityCount > 0 && (
						<section>
							{`${
								totalEntityCount > 1 ? labels.flowChartControl.controlsCounter : labels.flowChartControl.controlsCounter
							}`.replace('{0}', totalEntityCount)}
						</section>
					)}
				</section>
			</section>
		),
		[]
	);

	const data = useCallback((page, pageSize, searchText, sortColumn, selected) => {
		setPageSize(pageSize);
		setPage(page);
		let queryString = {
			page: page,
			pageSize: pageSize,
			includePagination: true,
			filterScots: entityUIDInt
		};
		if (!isNullOrWhitespace(searchText)) {
			queryString.searchString =
				isNullOrWhitespace(searchText) && isNullOrUndefined(searchText) ? '' : searchText.trim();
		}
		if (selected) {
			queryString.filterFlowchartStep = flowchartStep.id;
		}

		if (!flowchartStep.id && !selected) {
			return dispatch(getAllControls(engagementId, {resolve: true}, queryString));
		} else if (!flowchartStep.id && selected) {
			return Promise.resolve({
				status: 'success',
				data: [],
				page: 1,
				pages: 1,
				totalEntityCount: 0
			});
		} else {
			return dispatch(getAllControls(engagementId, {resolve: true}, queryString));
		}
	}, []);

	const onCheckChange = useCallback(
		(control, checked) => {
			let promise = null;
			if (checked) {
				let controlPayload = {
					controlId: control.id,
					documentId: documentId
				};
				promise = dispatch(
					relateControlToFlowChartStep(engagementId, flowchartId, flowchartStepId, controlPayload)
				).then(() => {
					flowchartControlRef.current.add(control.id);
					dispatch(
						updateStoreRelatedFlowchartStepControl({
							controlName: control.controlName,
							hasControlTesting: control.hasControlTesting,
							isLowerRisk: false,
							controlDesignEffectivenessTypeId: control.controlDesignEffectivenessTypeId,
							engagementId: engagementId,
							flowchartStepId: flowchartStepId,
							controlId: control.id
						})
					);
				});
			} else {
				promise = dispatch(
					unrelateControlFromFlowChartStep(engagementId, flowchartId, flowchartStepId, control.id)
				).then(() => {
					flowchartControlRef.current.delete(control.id);
					dispatch(
						updateStoreUnrelatedControlFromFlowchartStep({
							flowchartStepId: flowchartStepId,
							controlId: control.id
						})
					);
				});
			}
			return promise;
		},
		[dispatch, engagementId, flowchartId, flowchartStepId, documentId, flowchartControlRef]
	);

	let configuration = useMemo(
		() => ({
			noResultsLabel: labels.noResultsFound,
			data: (page, pageSize, searchString, sortColumn, selected) =>
				data(page, pageSize, searchString, sortColumn, selected),
			selected: (entity) => selected(entity),
			mapResponse: (response) => mapResponse(response),
			render: renderItemComponent,
			noDataLabel: labels.noResultsFound,
			onCheckBoxChange: onCheckChange
		}),
		[mapResponse, onCheckChange]
	);

	const mapResponse = (response) => {
		if (response) {
			let controlList = response.data && response.data.length > 0 ? response.data : [];
			return {
				page: response.page,
				pages: response.pages,
				totalEntityCount: response.totalEntityCount,
				data: controlList
			};
		}
	};

	return (
		<>
			{
				<Modal
					modalsize="medium"
					show={isModalOpen}
					title={labels.flowChartControl.headerName}
					onHide={onClickRelateModal}
					closeTitle={labels.cancelLabel}
					showFooter={false}
					modalLevel={modalLevelToast.level2}
				>
					<StyledSection>
						<RelateItemList
							componentHeader={componentHeader}
							listHeaders={relateListHeaders}
							search
							pagination
							optionsPerPage={specialBodyExtendedPagingOptions.options}
							page={page}
							pageSize={pageSize}
							configuration={configuration}
							showAddedLabel
							addedLabel={labels.added}
							searchPlaceholder={labels.searchPlaceholder}
							paginationIconHover={currentResource.pagingSvgHoverText}
							paginationShowText={labels.pagingShowtext}
							showSelectedOnlyCheckBox
							showSelectedOnlyText={labels.flowChartControl.showOnlyRelatedText}
							onPageChanged={(page) => {
								data(page);
							}}
						/>
					</StyledSection>
				</Modal>
			}
		</>
	);
}

const StyledSection = styled.section`
	height: 100%;
	/* .relate-list-toolbar {
		display: block;
		.additional-toolbar-controls {
			width: 100%;
			max-width: 100%;
			margin-left: 0;
			display: inline-flex;
			.motif-search-input-wrapper {
				width: var(--px-400);
			}
			.motif-checkbox-wrapper {
				margin-left: auto;
			}
		}
		.StyledPagination {
			display: flex;
			justify-content: end;
			flex: 1;
			padding: var(--px-10) 0;
		}
	} */

	.relate-list-body-wrapper {
		.relate-item-list-labelTxt {
			width: 85%;
			max-width: 85%;
		}
	}

	.RelateItemsListHeaderSelectedOnly {
		max-width: 100%;
		display: flex;
		align-items: center;
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			margin: var(--px-10) 0;
			max-width: 100%;
		}
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			.motif-checkbox-label {
				width: calc(100% - var(--px-20));
			}
		}
	}
	.RelateItemsListHeaderComponent {
		width: 100% !important;
		max-width: 80%;
	}
	.filter-wcgw-container {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) 0;
		.related-wcgw {
			display: flex;
			width: 100%;
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);

			.title {
				margin-right: var(--px-4);
				font-size: var(--px-14);
				font-weight: 300;
			}

			.ellipses {
				font-size: var(--px-14);
				font-weight: 400;
				width: 92%;
			}
		}
	}
	.wcgw-name {
		margin-left: var(--px-33);
		font-weight: bold;
	}
	.wcgw-count {
		margin-left: auto;
		margin-right: var(--px-20);
		min-width: var(--px-100);
		text-align: right;
	}
	.wcgw-header {
		display: flex;
	}
	.motif-checkbox {
		display: flex;
		justify-content: flex-end;
		width: 100%;
	}
`;
